/**
 * Search Intent Detector
 * 
 * Detects user intent to search for products from text input.
 * Patterns are synchronized with backend (api/src/functions/getOpenAIResponseStream.ts)
 * 
 * IMPORTANT: Keep patterns in sync with backend!
 * If you update patterns here, update them in the backend too.
 * 
 * @author Heseda AI Team
 * @version 1.0
 */

/**
 * Search intent patterns by language.
 * These patterns detect explicit user requests for product search.
 * 
 * Following industry trends (Siri, Alexa, Google Assistant):
 * - Require explicit action phrases
 * - Avoid proactive/surprise triggers
 * - Clear user intent mapping
 */
export const SEARCH_INTENT_PATTERNS = {
  ko: [
    /추천\s*(해|좀|해줘|해주세요)/i,   // 추천해줘, 추천해주세요
    /뭐가?\s*(좋을까|좋아|괜찮)/i,      // 뭐가 좋을까
    /어떤\s*(제품|거)\s*(좋|쓰면|있)/i, // 어떤 제품이 좋아
    /검색\s*(해|좀)/i,                  // 검색해줘 (primary trigger)
    /찾아\s*(줘|봐|주세요)/i,           // 찾아줘
    /살\s*(만한|거|제품)/i,             // 살만한 제품
    /구매|구입|사고\s*싶/i,             // 구매하고 싶어
    /웹\s*검색/i,                       // 웹 검색
  ],
  en: [
    /\b(?:recommend|suggest)\w*\b.*\b(?:products?|moisturizer|serum|cleanser|sunscreen|toner|cream|retinol)\b|\b(?:products?|moisturizer|serum|cleanser|sunscreen|toner|cream|retinol)\b.*\b(?:recommend|suggest)\w*\b|\bfind\b.*\bproducts?\b/i,
    /what.*should.*i.*(use|try|get|buy)/i,
    /show.*me.*product/i,
    /search.*for.*\b(?:products?|serums?|creams?|moisturizers?|cleansers?|sunscreens?|toners?|masks?|treatments?|ingredients?|snail|mucin|retinol|niacinamide|SPF|hyaluronic|vitamin|collagen|peptide|ceramide|cica|centella)\b/i,
    /\bfind\s+me\b.*\b(?:products?|serums?|creams?|moisturizers?|cleansers?|sunscreens?|toners?|masks?|treatments?|ingredients?|snail|mucin|retinol|niacinamide|SPF|hyaluronic|vitamin|collagen|peptide|ceramide|cica|centella)\b/i,
    /\blooking\s+for\b.*\b(?:products?|serums?|creams?|moisturizers?|cleansers?|sunscreens?|toners?|masks?|treatments?|ingredients?|snail|mucin|retinol|niacinamide|SPF|hyaluronic|vitamin|collagen|peptide|ceramide|cica|centella)\b/i,
    /best.*product/i,
    /product.*for.*my/i,
    /can.*you.*find/i,
    /\b(build|create|make)\s+(me\s+)?(a\s+)?routine/i,
    /\b(?:products?|serums?|creams?|moisturizers?|cleansers?|sunscreens?|toners?|masks?|treatments?|retinol|niacinamide|vitamin\s*c|hyaluronic|ceramide|centella|snail|mucin)\s+vs\.?\s+(?:products?|serums?|creams?|moisturizers?|cleansers?|sunscreens?|toners?|masks?|treatments?|retinol|niacinamide|vitamin\s*c|hyaluronic|ceramide|centella|snail|mucin)\b/i,
    /\b(need|want)\s+(a\s+)?([\w\s]*?)\b(moisturizer|serum|cleanser|sunscreen|toner|cream|retinol)\b/i,
    /web\s*search/i,
  ],
} as const;

export type SupportedLanguage = string;

/**
 * Detect if text contains search intent
 * 
 * @param text - User input text (can be from text chat or voice transcript)
 * @param language - Current language
 * @param profileCompletion - User's profile completion percentage (0-100)
 * @returns Whether search should be triggered
 * 
 * @example
 * ```ts
 * const shouldSearch = detectSearchIntent('모공 제품 추천해줘', 'ko', 80);
 * // Returns: true
 * ```
 */
export function detectSearchIntent(
  text: string,
  language: SupportedLanguage,
  profileCompletion: number = 0
): boolean {
  // Profile must be at least 50% complete for search to work well
  // This ensures we have enough context for relevant results
  if (profileCompletion < 50) {
    return false;
  }

  const patterns = SEARCH_INTENT_PATTERNS[language as keyof typeof SEARCH_INTENT_PATTERNS] || SEARCH_INTENT_PATTERNS.en;
  return patterns.some((pattern: RegExp) => pattern.test(text));
}

/**
 * Get search trigger response message
 * Used when voice mode detects search intent - the AI should announce
 * before showing results (following industry UX patterns)
 * 
 * @param language - Current language
 * @returns Announcement message
 */
export function getSearchTriggerMessage(language: SupportedLanguage): string {
  return language === 'ko'
    ? '네, 맞춤 제품을 검색해 드릴게요.'
    : "Sure, I'll search for products for you.";
}

/**
 * Get search trigger announcement for voice
 * Shorter version for voice output
 * 
 * @param language - Current language
 * @returns Short voice announcement
 */
export function getVoiceSearchAnnouncement(language: SupportedLanguage): string {
  return language === 'ko'
    ? '제품을 검색할게요.'
    : "Searching for products.";
}
